import { FC, useMemo } from 'react';
import { format } from 'date-fns';
import { useLocation } from 'react-router-dom';
import { useTranslate } from 'hooks/useTranslate';
import { dateFormat } from 'constants/formats';
import { Partner } from 'types';

import GoBackButton from 'modules/_shared/components/Buttons/GoBackButton';
import Breadcrumb, {
  BreadcrumbItem,
} from 'modules/_shared/components/Breadcrumb';
import { TradingOffer, TradingOfferStatus } from '../../../types/TradingOffer';
import TradingOfferStatusBadge from './TradingOfferStatusBadge';

type TradingOfferDetailHeaderProps = {
  offer: TradingOffer;
  offerReference: string;
  offerStatus: TradingOfferStatus;
  societyId: string;
  societyName: string;
};

const TradingOfferDetailHeader: FC<TradingOfferDetailHeaderProps> = ({
  offer,
  offerReference,
  offerStatus: status,
  societyId,
  societyName,
}) => {
  const { t } = useTranslate();
  const location = useLocation();
  const partnerQuery = new URLSearchParams(location.search).get('partnerId');
  const pq = partnerQuery ? `?partnerId=${partnerQuery}` : '';

  const isSellOffer = offer.type === 'SELL';

  const partnerName = useMemo(() => {
    if (typeof offer.partner === 'string') return '';
    return (offer.partner as Partner)?.name || '';
  }, [offer.partner]);

  const offerTitle = isSellOffer ? t('SellOffer') : t('BuyOffer');

  const breadcrumbItems: BreadcrumbItem[] = useMemo(
    () => [
      { label: t('Dashboard'), to: '/' },
      { label: societyName, to: `/sociedad/${societyId}` },
      { label: t('EquityMarket'), to: `/mercado-de-acciones/${societyId}${pq}` },
      { label: t('Offers'), to: `/ofertas/${societyId}${pq}` },
      { label: offerReference, isActive: true },
    ],
    [t, societyName, societyId, offerReference, pq]
  );

  return (
    <>
      <div className="nk-block-head-content mb-3">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="nk-block-head page-header-subtitle">
        <div className="nk-block-between">
          <div className="nk-block-head-content">
            <h3 className="nk-block-title page-title">
              {offerTitle}
              <span className="badge badge-outline badge-primary ml-2">
                {offerReference}
              </span>
            </h3>
            <div className="nk-block-des text-soft">
              <p>
                {t('PublishedBy')} {partnerName} {t('On').toLowerCase()}{' '}
                {format(new Date(offer.createdAt), dateFormat)}
              </p>
            </div>
          </div>

          <div className="nk-block-head-content">
            <div className="toggle-wrap nk-block-tools-toggle">
              <div className="toggle-expand-content" data-content="pageMenu">
                <ul className="nk-block-tools g-3">
                  <li>
                    <TradingOfferStatusBadge status={status} />
                  </li>
                  <li>
                    <GoBackButton />
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TradingOfferDetailHeader;
